/**
 * Match the two bold words ("Power" and "power") to identical visual width
 * via letter-spacing — they're the same word semantically, so they should
 * look identical typographically. The natural Power (capital P) is a few
 * pixels wider than power (lowercase p); we add minimal letter-spacing to
 * power so the bottom row mirrors the top.
 *
 * The middle word ("attracts") is left alone at its natural width — wider
 * in the middle creates a diamond-shape composition that reads as symmetric
 * top-to-bottom.
 *
 * Re-runs on `document.fonts.ready` (metrics shift when web fonts settle)
 * and on resize.
 */

const SELECTOR = '#phrase .word.strong[data-stretch]';

function letterCount(el: HTMLElement): number {
  const explicit = el.dataset.letters;
  if (explicit) {
    const n = parseInt(explicit, 10);
    if (Number.isFinite(n) && n > 0) return n;
  }
  let text = el.textContent ?? '';
  const sup = el.querySelector<HTMLElement>('sup');
  if (sup) text = text.replace(sup.textContent ?? '', '');
  return text.trim().length;
}

function apply(): void {
  const els = Array.from(document.querySelectorAll<HTMLElement>(SELECTOR));
  if (els.length < 2) return;

  els.forEach((el) => {
    el.style.letterSpacing = '';
    el.style.transform = '';
  });
  void els[0]?.offsetWidth;

  const widths = els.map((el) => el.getBoundingClientRect().width);
  const target = Math.max(...widths);
  if (target <= 0) return;

  els.forEach((el, i) => {
    const natural = widths[i] ?? 0;
    if (natural >= target - 0.5) return;
    const letters = letterCount(el);
    if (letters < 2) return;
    const extra = target - natural;
    const spacing = extra / (letters - 1);
    el.style.letterSpacing = `${spacing.toFixed(2)}px`;
    el.style.transform = `translateX(${(spacing / 2).toFixed(2)}px)`;
  });
}

export function installJustifyWords(): void {
  const run = () => requestAnimationFrame(apply);

  run();

  if (typeof document !== 'undefined' && 'fonts' in document) {
    document.fonts.ready.then(run).catch(() => {
      /* fallback metrics are fine */
    });
  }

  let resizeRaf = 0;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(apply);
  });
}
